import { useState } from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import type { VCard } from '@/api/account';
import { AccountAvatar } from './AccountAvatar';

// Fixed VNETWORK brand palette — this is a branded business card (a print-like
// artifact), so the colours stay identical in light and dark mode.
const BRAND_RED = '#C8102E';
const BRAND_PINK = '#FFF1F2';

/** Public share URL for a VCard (QR / copy-link / "view directly" all use this). */
export const vcardPublicUrl = (uuid: string) => `${window.location.origin}/vcard/${uuid}`;

/**
 * The branded VCard visual (datacentral-client `VCardPublicView`, preview mode):
 * red header, avatar, name/position/description, QR to the public card, contact
 * action buttons, Copy-link. Reused on the VCard list "View" and elsewhere.
 */
export function VCardPreview({ vcard }: { vcard: VCard }) {
  const [copied, setCopied] = useState(false);
  const url = vcardPublicUrl(vcard.uuid);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure context) — ignore */
    }
  };

  const actions = [
    vcard.address?.trim() && { key: 'address', label: 'Address', icon: <MapPin size={22} />, href: undefined as string | undefined },
    vcard.phone?.trim() && { key: 'phone', label: 'Phone', icon: <Phone size={22} />, href: `tel:${vcard.phone.replace(/\s+/g, '')}` },
    vcard.email?.trim() && { key: 'email', label: 'Email', icon: <Mail size={22} />, href: `mailto:${vcard.email}` },
  ].filter(Boolean) as { key: string; label: string; icon: React.ReactNode; href?: string }[];

  return (
    <div style={{ width: '100%', maxWidth: 448, margin: '0 auto', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--vn-shadow-lg)', background: BRAND_PINK }}>
      <div style={{ position: 'relative', background: BRAND_RED, padding: '20px 20px 48px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontWeight: 800, fontSize: 28, lineHeight: 1.1 }}>Leading<br />Cloud<br />Security</h2>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 'var(--vn-font-size-xs)', marginTop: 8, display: 'block' }}>www.vnetwork.vn</span>
          </div>
          <div style={{ textAlign: 'right', marginTop: 6 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: '0.08em' }}>VNETWORK</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase' }}>safe and saved</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 20px 24px', marginTop: -48, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: `4px solid ${BRAND_PINK}`, background: '#fff' }}>
          <AccountAvatar name={vcard.name} src={vcard.avatar} size={104} />
        </div>
        <div style={{ marginTop: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#1a1a1a', textAlign: 'center' }}>{(vcard.name || '').toUpperCase()}</div>
        {vcard.position ? <div style={{ fontSize: 'var(--vn-font-size-sm)', color: '#444', textTransform: 'uppercase', letterSpacing: '0.02em', marginTop: 4, textAlign: 'center' }}>{vcard.position}</div> : null}
        {vcard.description ? <div style={{ fontSize: 'var(--vn-font-size-sm)', color: '#777', fontStyle: 'italic', marginTop: 8, textAlign: 'center' }}>{vcard.description}</div> : null}

        {vcard.uuid ? <div style={{ marginTop: 20, background: '#fff', padding: 12, borderRadius: 12 }}><QRCodeSVG value={url} size={180} /></div> : null}

        {actions.length > 0 ? (
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, width: '100%' }}>
            {actions.map((a) => (
              <a key={a.key} href={a.href} onClick={a.href ? undefined : (e) => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                <span style={{ width: 56, height: 56, borderRadius: 12, background: BRAND_RED, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>{a.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase' }}>{a.label}</span>
              </a>
            ))}
          </div>
        ) : null}

        <button type="button" onClick={copyLink} style={{ marginTop: 24, width: '100%', borderRadius: 12, border: 'none', background: BRAND_RED, color: '#fff', padding: '12px 0', fontSize: 'var(--vn-font-size-sm)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', cursor: 'pointer' }}>
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <div style={{ marginTop: 16, fontSize: 11, color: '#B9B9B9', textAlign: 'center' }}>©{new Date().getFullYear()} VNETWORK JSC | Leading Cloud Security</div>
      </div>
    </div>
  );
}
